// src/modules/gyms/components/branch-page/BranchOpeningHours.tsx
import { getTodayKey, isCurrentlyOpen } from "../../utils/time.utils";

interface BranchOpeningHoursProps {
  openingHours?: Record<string, { start_time: string; end_time: string }>;
  defaultFrom: string;
  defaultTo: string;
}

const DAYS = [
  { key: "monday", label: "Thứ 2" },
  { key: "tuesday", label: "Thứ 3" },
  { key: "wednesday", label: "Thứ 4" },
  { key: "thursday", label: "Thứ 5" },
  { key: "friday", label: "Thứ 6" },
  { key: "saturday", label: "Thứ 7" },
  { key: "sunday", label: "Chủ nhật" },
];

const isValidTime = (time: string): boolean => {
  return time !== "00:00" && time !== "0:00" && time !== "";
};

export const BranchOpeningHours = ({
  openingHours,
  defaultFrom,
  defaultTo,
}: BranchOpeningHoursProps) => {
  const todayKey = getTodayKey();
  const hasData = openingHours && Object.keys(openingHours).length > 0;

  const getHour = (dayKey: string, type: "start" | "end"): string => {
    if (!hasData) return type === "start" ? defaultFrom : defaultTo;
    const dayData = openingHours[dayKey];
    if (!dayData) return type === "start" ? defaultFrom : defaultTo;
    const time = type === "start" ? dayData.start_time : dayData.end_time;
    return isValidTime(time)
      ? time
      : type === "start"
        ? defaultFrom
        : defaultTo;
  };

  const isDayClosed = (dayKey: string): boolean => {
    if (!hasData) return false;
    const dayData = openingHours[dayKey];
    if (!dayData) return false;
    return dayData.start_time === "00:00" && dayData.end_time === "00:00";
  };

  const allSame = DAYS.every((day) => {
    const from = getHour(day.key, "start");
    const to = getHour(day.key, "end");
    return from === defaultFrom && to === defaultTo && !isDayClosed(day.key);
  });

  const todayFrom = getHour(todayKey, "start");
  const todayTo = getHour(todayKey, "end");
  const openNow = !isDayClosed(todayKey) && isCurrentlyOpen(todayFrom, todayTo);

  if (!hasData || allSame) {
    return (
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body">
          <h6 className="fw-bold mb-3" style={{ fontSize: "1rem" }}>
            Giờ mở cửa
          </h6>
          <div className="d-flex align-items-center gap-2 mb-1">
            <i
              className="bi bi-clock text-secondary"
              style={{ fontSize: "1.25rem" }}
            />
            <span className="fw-semibold" style={{ fontSize: "0.95rem" }}>
              {defaultFrom} – {defaultTo}
            </span>
            <span
              className={`badge rounded-pill px-2 py-1 ${openNow ? "bg-success" : "bg-secondary"}`}
              style={{ fontSize: "0.75rem", fontWeight: 600 }}
            >
              <i
                className={`bi ${openNow ? "bi-check-circle-fill" : "bi-x-circle-fill"} me-1`}
              />
              {openNow ? "Đang mở" : "Đã đóng"}
            </span>
          </div>
          <div
            className="text-muted d-flex align-items-center gap-1"
            style={{ fontSize: "0.85rem" }}
          >
            <i className="bi bi-calendar-check" />
            <span>Áp dụng tất cả các ngày</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <h6 className="fw-bold mb-3" style={{ fontSize: "1.15rem" }}>
          Giờ mở cửa
          <span
            className="badge bg-soft-warning text-warning ms-2"
            style={{ fontSize: "0.7rem" }}
          >
            <i className="bi bi-info-circle me-1" />
            Theo ngày
          </span>
        </h6>

        <div className="d-flex flex-column gap-1">
          {DAYS.map((day) => {
            const from = getHour(day.key, "start");
            const to = getHour(day.key, "end");
            const isToday = day.key === todayKey;
            const closed = isDayClosed(day.key);

            return (
              <div
                key={day.key}
                className={`d-flex justify-content-between align-items-center py-1 px-2 rounded ${
                  isToday ? "bg-light border-start border-3 border-primary" : ""
                }`}
                style={isToday ? { paddingLeft: "0.75rem" } : {}}
              >
                <div className="d-flex align-items-center gap-2">
                  <span className="text-secondary" style={{ fontSize: "1rem" }}>
                    {day.label}
                  </span>
                  {isToday && (
                    <span
                      className="badge bg-primary rounded-pill px-2"
                      style={{ fontSize: "0.75rem" }}
                    >
                      Hôm nay
                    </span>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  {closed ? (
                    <span
                      className="text-muted fw-medium"
                      style={{ fontSize: "1rem" }}
                    >
                      Nghỉ
                    </span>
                  ) : (
                    <span className="fw-medium" style={{ fontSize: "1rem" }}>
                      {from} – {to}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-muted small mt-2">
          <i className="bi bi-clock-history me-1" />
          Giờ có thể thay đổi theo ngày
        </div>
      </div>
    </div>
  );
};
